<template>
  <section :class="$style.configVisualizerTabs">
    <h2>Simulated config</h2>
    <el-tabs v-model="simulationType">
      <el-tab-pane label="Classes" name="classes">
        <Toolbar :class="$style.toolbar">
          <template #start>
            <SelectButton
              v-model="displayMode"
              :class="$style.displayTypeToggle"
              :options="Object.values(DISPLAY_MODES)"
              :allow-empty="false"
              data-key="val"
              option-value="val"
            >
              <template #option="slotProps">
                <i
                  v-if="slotProps.option.icon"
                  :class="[slotProps.option.icon, $style.displayTypeToggleIcon]"
                  class="pi"
                ></i>
                {{ slotProps.option.label }}
              </template>
            </SelectButton>
          </template>
          <template #end>
            <Button
              :class="[$style.copySimulationUrlButton, $style.copyButton]"
              icon="pi pi-clone"
              label="Copy Simulation URL"
              severity="info"
              outlined
              @click="copyValue(props.simulationUrl)"
            />
            <Button
              :class="$style.copyButton"
              icon="pi pi-clone"
              label="Copy JSON"
              severity="info"
              outlined
              @click="copyValue(props.rawSimulation)"
            />
          </template>
        </Toolbar>
        <ConfigVisualizer v-if="shouldDisplayFormattedConfig()" :config-data="props.simulation" />
        <div v-if="shouldDisplayRawConfig()">
          <p>This is the result sent by the Simulation:</p>
          <pre :class="$style.rawDisplay">{{ props.rawSimulation }}</pre>
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="props.rawOfferSimulation" label="Offers" name="offers">
        <Toolbar :class="$style.toolbar">
          <template #start>
            <SelectButton
              v-model="displayMode"
              :class="$style.displayTypeToggle"
              :options="Object.values(DISPLAY_MODES)"
              :allow-empty="false"
              data-key="val"
              option-value="val"
            >
              <template #option="slotProps">
                <i
                  v-if="slotProps.option.icon"
                  :class="[slotProps.option.icon, $style.displayTypeToggleIcon]"
                  class="pi"
                ></i>
                {{ slotProps.option.label }}
              </template>
            </SelectButton>
          </template>
          <template #end>
            <Button
              :class="[$style.copySimulationUrlButton, $style.copyButton]"
              icon="pi pi-clone"
              label="Copy Simulation URL"
              severity="info"
              outlined
              @click="copyValue(props.offerSimulationUrl)"
            />
            <Button
              :class="$style.copyButton"
              icon="pi pi-clone"
              label="Copy JSON"
              severity="info"
              outlined
              @click="copyValue(props.rawOfferSimulation)"
            />
          </template>
        </Toolbar>
        <OfferConfigVisualizer v-if="shouldDisplayFormattedConfig()" :config-data="props.offerSimulation" />
        <div v-if="shouldDisplayRawConfig()">
          <p>This is the result sent by the Simulation:</p>
          <pre :class="$style.rawDisplay">{{ props.rawOfferSimulation }}</pre>
        </div>
      </el-tab-pane>
      <el-tab-pane v-if="props.rawShopSimulation" label="Shop" name="shop">
        <Toolbar :class="$style.toolbar">
          <template #start>
            <SelectButton
              v-model="displayMode"
              :class="$style.displayTypeToggle"
              :options="Object.values(DISPLAY_MODES)"
              :allow-empty="false"
              data-key="val"
              option-value="val"
            >
              <template #option="slotProps">
                <i
                  v-if="slotProps.option.icon"
                  :class="[slotProps.option.icon, $style.displayTypeToggleIcon]"
                  class="pi"
                ></i>
                {{ slotProps.option.label }}
              </template>
            </SelectButton>
          </template>
          <template #end>
            <Button
              :class="[$style.copySimulationUrlButton, $style.copyButton]"
              icon="pi pi-clone"
              label="Copy Simulation URL"
              severity="info"
              outlined
              @click="copyValue(props.shopSimulationUrl)"
            />
            <Button
              :class="$style.copyButton"
              icon="pi pi-clone"
              label="Copy JSON"
              severity="info"
              outlined
              @click="copyValue(props.rawShopSimulation)"
            />
          </template>
        </Toolbar>
        <ShopConfigVisualizer v-if="shouldDisplayFormattedConfig()" :config-data="props.shopSimulation" />
        <div v-if="shouldDisplayRawConfig()">
          <p>This is the result sent by the Simulation:</p>
          <pre :class="$style.rawDisplay">{{ props.rawShopSimulation }}</pre>
        </div>
      </el-tab-pane>
    </el-tabs>
  </section>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import SelectButton from "primevue/selectbutton";
import Toolbar from "primevue/toolbar";
import { ref } from "vue";

import useNotifications from "@/hooks/notifications";
import ConfigVisualizer from "@/modules/Simulation/components/ConfigVisualizer.vue";
import OfferConfigVisualizer from "@/modules/Simulation/components/OfferConfigVisualizer.vue";
import ShopConfigVisualizer from "@/modules/Simulation/components/ShopConfigVisualizer.vue";
import { SimulationClass, SimulationOffer, SimulationShop } from "@/modules/Simulation/types";

const DISPLAY_MODES = {
  classes: {
    val: "classes",
    label: "Summary",
    icon: "",
  },
  raw: {
    val: "raw",
    label: "JSON",
    icon: "pi-code",
  },
};

const SIMULATION_TYPE = {
  classes: "classes",
  offer: "offers",
  shop: "shop",
};

const displayMode = ref(DISPLAY_MODES.classes.val);
const simulationType = ref(SIMULATION_TYPE.classes);
const props = defineProps<{
  simulation: SimulationClass[];
  simulationUrl: string;
  rawSimulation: string;
  offerSimulation: SimulationOffer[];
  offerSimulationUrl: string;
  rawOfferSimulation: string;
  shopSimulation: SimulationShop;
  shopSimulationUrl: string;
  rawShopSimulation: string;
}>();

const { notifySuccess } = useNotifications();

const shouldDisplayFormattedConfig = () => displayMode.value === DISPLAY_MODES.classes.val;
const shouldDisplayRawConfig = () => displayMode.value === DISPLAY_MODES.raw.val;

const copyValue = async (value: string) => {
  navigator.clipboard.writeText(value);
  notifySuccess("Copied!");
};
</script>

<style module lang="scss">
.configVisualizerTabs {
  h2 {
    margin-bottom: size("L");
  }

  .rawDisplay {
    font-size: font("S");
  }

  .el-tabs__header {
    margin: 0;
  }
}

.toolbar {
  background: white;
  border: 0;
  padding: 0;
}

.copyButton {
  color: #0055ff;
  border: 1px solid #85b2f9;
  padding: 7px;
}

.copySimulationUrlButton {
  margin-right: 5px;
}

.displayTypeToggle {
  margin: 5px 0;
}

.displayTypeToggleIcon {
  margin-right: 5px;
}
</style>
